import React, { createContext, useCallback, useContext } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { EmployerDetail } from 'app/types';
import { useEmployerGetDetailQuery } from 'app/hooks/api';
import { StoreActions, useStoreDispatch } from 'app/store';

type EmployerDetailContextValue = {
  employerDetail: EmployerDetail | undefined;
  isLoading: boolean;
  refetch: UseQueryResult['refetch'];
};

const EmployerDetailContext = createContext<EmployerDetailContextValue | undefined>(undefined);

type EmployerDetailProviderProps = {
  employerId: string;
  children: React.ReactNode;
};

export const EmployerDetailProvider = ({ employerId, children }: EmployerDetailProviderProps) => {
  const dispatch = useStoreDispatch();

  // Sync fetched data into the Redux store for legacy components
  const onSuccess = useCallback(
    (data: EmployerDetail) => {
      dispatch(StoreActions.EmployerActions.employer.fulfilledData(data));
      if (data.owner?.ownerId) {
        dispatch(StoreActions.OwnerActions.owner.fulfilledData(data.owner));
      }
    },
    [dispatch],
  );

  const {
    data: employerDetail,
    isLoading,
    refetch,
  } = useEmployerGetDetailQuery({
    variables: { employerId },
    options: { onSuccess },
  });

  return (
    <EmployerDetailContext.Provider value={{ employerDetail, isLoading, refetch }}>
      {children}
    </EmployerDetailContext.Provider>
  );
};

export const useEmployerDetailContext = (): EmployerDetailContextValue => {
  const ctx = useContext(EmployerDetailContext);
  if (!ctx) {
    throw new Error('useEmployerDetailContext must be used within EmployerDetailProvider');
  }
  return ctx;
};
